'use client';

import React, { Fragment } from 'react';

import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { Container } from '@/components/Container';
import { HELP_ARTICLES } from '@/constants/helpUrls';
import {
  whyChooseDepopShippingUrl,
  whyChooseSectionData,
} from '@/constants/shippingLanding';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { type SupportedLocation } from '@/modules/location/constants';
import { UserType } from '@/modules/shipping/types';
import { getTranslationWithLink } from '@/utils/getTranslationWithLink/getTranslationWithLink';

type Props = {
  location: SupportedLocation;
};

export function WhyChooseDepopShipping({ location }: Props) {
  const t = useTranslations('shipping');
  const { sendActivityTrackerEvent } = useActivityTracker();

  const whyChooseSectionDataFilteredByLocale = whyChooseSectionData.map(
    ({ subheadingKey, bullets }) => ({
      subheadingKey,
      bullets: bullets.filter(
        (bullet) => !bullet.locales || bullet.locales.includes(location)
      ),
    })
  );

  function handleProtectionLinkClick() {
    return sendActivityTrackerEvent(
      ActivityTrackerEventType.PROTECTION_LINK_ACTION,
      {
        userType: UserType.Seller,
        schemaVersion: '1.0',
      }
    );
  }

  return (
    <section className={styles.section}>
      <Container className={styles.container}>
        <div className={styles.headingWrapper}>
          <Heading as="h2">
            {t('ShippingPage.WhyChooseDepopShipping.Title')}
          </Heading>
        </div>
        <span className={styles.textWrapper}>
          {whyChooseSectionDataFilteredByLocale.map(
            ({ subheadingKey, bullets }) => (
              <Fragment key={subheadingKey}>
                <Body variant="md-bold" className={styles.subheading}>
                  {t(subheadingKey)}
                </Body>
                <ul className={styles.list}>
                  {bullets.map((bullet) => (
                    <li key={bullet.translationKey} className={styles.listItem}>
                      <Body as="div" variant="md-regular">
                        {bullet.linkUrl
                          ? t.rich(
                              bullet.translationKey,
                              getTranslationWithLink(
                                bullet.linkUrl,
                                { content: '', link: styles.protectionLink },
                                bullet.linkUrl ===
                                  HELP_ARTICLES.DEPOP_PROTECTION_FOR_SELLERS
                                  ? { onClick: handleProtectionLinkClick }
                                  : undefined
                              )
                            )
                          : t(bullet.translationKey)}
                      </Body>
                    </li>
                  ))}
                </ul>
              </Fragment>
            )
          )}
          <Body className={styles.disclaimerText}>
            {t('ShippingPage.LegalDisclaimer')}
          </Body>
        </span>
        <div className={styles.imageWrapper}>
          <img
            src={whyChooseDepopShippingUrl}
            alt="why choose depop shipping"
          />
        </div>
      </Container>
    </section>
  );
}
